import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { Toggle } from '@carbon/react';
import cx from 'clsx';
import _ from 'lodash';

import { useNotifications } from 'core/notifications';
import { routes } from 'core/routing';
import OutputsAccordion from 'features/outputs/components/OutputsAccordion';
import OutputsAccordionItem from 'features/outputs/components/OutputsAccordionItem';
import OutputsActions from 'features/outputs/components/OutputsActions';
import SaveProjectAsModal from 'features/outputs/components/SaveProjectAsModal';
import RegularSpacesTable from 'features/outputs/RegularSpacesTable';
import SpecialtySpacesAccordion from 'features/outputs/SpecialtySpacesAccordion';
import { OUTPUT_CONFIGURATION_TYPE } from 'models/outputConfigurationType';
import { SPACE_CATEGORY } from 'models/spaces';
import { SUB_GROUP_OVERRIDE } from 'models/subGroupOverride';
import useMutateSaveProjectAs from 'shared/api/projects/useMutateSaveProjectAs';
import useMutateSaveProjectOutputConfigurations from 'shared/api/projects/useMutateSaveProjectOutputConfigurations';
import useMutateUpdateProjectMeasurementSystem from 'shared/api/projects/useMutateUpdateProjectMeasurementSystem';
import useMutateUpdateProjectSiteSpecificSpaces from 'shared/api/projects/useMutateUpdateProjectSiteSpecificSpaces';
import useProjectOutputsQuery from 'shared/api/projects/useProjectOutputsQuery';
import useProjectQuery, { createUseProjectQueryKey } from 'shared/api/projects/useProjectQuery';
import useSpacesQuery from 'shared/api/spaces/useSpacesQuery';
import useSpecialtySpacesQuery from 'shared/api/specialty-spaces/useSpecialtySpacesQuery';
import useSpecialtySpacesRoomsQuery from 'shared/api/specialty-spaces/useSpecialtySpacesRoomsQuery';
import Link from 'shared/components/Link';
import PageDescription from 'shared/components/PageDescription';
import ProjectBreadcrumbs, { PROJECT_BREADCRUMBS_PAGE } from 'shared/components/ProjectBreadcrumbs';
import SiteSpecificSpaces from 'shared/site-specific-spaces/SiteSpecificSpaces';
import useSiteSpecificSpacesController from 'shared/site-specific-spaces/useSiteSpecificSpacesController';
import { convertToCurrentSystem, convertToStandardSystem } from 'utils/units';
import Overview from './Overview';
import styles from 'theme/features/outputs/outputs.module.scss';

const createRowEdits = (configuration, measurementSystem) =>
  Object.fromEntries(
    Object.entries(_.groupBy(configuration, 'type')).map(([type, configurations]) => [
      type,
      _.mapValues(_.keyBy(configurations, 'spaceId'), (value) => ({
        value:
          'unitArea' in value
            ? { ...value, unitArea: convertToCurrentSystem(value.unitArea, measurementSystem) }
            : value,
        isSaved: true
      }))
    ])
  );

const DEFAULT_WORKSTATION_EDITS = {
  cic: {
    values: [],
    saved: true
  },
  other: {
    values: [],
    saved: true
  }
};

const Outputs = ({ projectId, project, isProjectLoading, isExport = false }) => {
  const navigate = useNavigate();

  const queryClient = useQueryClient();

  const { showSuccessNotification, showErrorNotification } = useNotifications();

  const { refetch: refetchProject } = useProjectQuery(projectId);

  const [measurementSystem, setMeasurementSystem] = useState(null);
  const [isShowingCustomizations, setIsShowingCustomizations] = useState(false);
  const [isSaveAsModalOpen, setIsSaveAsModalOpen] = useState(false);
  const [rowEdits, setRowEdits] = useState({});
  const initialRowEditsRef = useRef({});
  const [workstationEdits, setWorkstationEdits] = useState(DEFAULT_WORKSTATION_EDITS);
  const { data: spaces, isLoading: isSpacesLoading } = useSpacesQuery();
  const {
    data: outputs,
    isLoading: isOutputsLoading,
    isRefetching: isRefetchingOutputs,
    refetch: refetchOutputs
  } = useProjectOutputsQuery(projectId);
  const { data: specialtySpaces, isLoading: isSpecialtySpacesLoading } = useSpecialtySpacesQuery();
  const { data: specialtySpacesRooms, isLoading: isSpecialtySpacesRoomsLoading } = useSpecialtySpacesRoomsQuery();
  const specialtySpacesRoomsById = useMemo(() => _.keyBy(specialtySpacesRooms, 'id'), [specialtySpacesRooms]);

  const updateProjectSiteSpecificSpacesMutation = useMutateUpdateProjectSiteSpecificSpaces({
    onError: () => showErrorNotification({ description: 'Failed to update site specific spaces' }),
    onSuccess: (project) => {
      queryClient.setQueryData(createUseProjectQueryKey(projectId), project);
      showSuccessNotification({ description: 'Project site specific spaces updated successfully' });
    }
  });

  const {
    spaces: siteSpecificSpaces,
    add: addSiteSpecificSpace,
    edit: editSiteSpecificSpace,
    remove: removeSiteSpecificSpace,
    reset: resetSiteSpecificSpaces,
    onSubmit: onSubmitSiteSpecificSpaces
  } = useSiteSpecificSpacesController({
    projectId,
    values: project?.siteSpecificSpaces ?? [],
    onSubmit: (spaces, isDirty) => {
      if (isDirty) {
        updateProjectSiteSpecificSpacesMutation.mutate({
          id: projectId,
          siteSpecificSpaces: spaces
        });
      }
    }
  });

  const saveProjectOutputConfigurationMutation = useMutateSaveProjectOutputConfigurations({
    onError: () => showErrorNotification({ description: 'Failed to update project outputs' }),
    onSuccess: async () => {
      showSuccessNotification({ description: 'Project outputs updated successfully' });

      await Promise.all([refetchOutputs(), refetchProject()]);
    }
  });

  const saveProjectAsMutation = useMutateSaveProjectAs({
    onError: () => showErrorNotification({ description: 'Failed to save the project' }),
    onSuccess: ({ projectId: newProjectId }) => {
      showSuccessNotification({ description: 'Project saved as successfully' });
      navigate(routes.projectOutputs(newProjectId));
    }
  });

  const updateProjectMeasurementSystemMutation = useMutateUpdateProjectMeasurementSystem({
    onSuccess: () => refetchProject(),
    onError: () => showErrorNotification({ description: 'Failed to update measurement system' })
  });

  const spacesByCategory = useMemo(() => _.groupBy(spaces, 'category'), [spaces]);
  const outputsBySpaceId = useMemo(() => _.keyBy(outputs?.outputs, 'spaceId'), [outputs]);

  const isLoading =
    isSpacesLoading ||
    isProjectLoading ||
    isOutputsLoading ||
    isSpecialtySpacesLoading ||
    isSpecialtySpacesRoomsLoading;

  useEffect(() => {
    if (project && !isOutputsLoading && !isRefetchingOutputs) {
      const newEdits = outputs ? createRowEdits(outputs.configurations.spaces, project.details.measurementSystem) : {};

      setRowEdits(newEdits);
      initialRowEditsRef.current = newEdits;

      const cicPopulationIds = project.employeePopulation
        .filter((p) => p.subGroup.override === SUB_GROUP_OVERRIDE.cic)
        .map((p) => p.id);

      setWorkstationEdits(
        outputs
          ? {
              cic: {
                saved: true,
                values: outputs.configurations.workstations.filter((w) =>
                  cicPopulationIds.includes(w.employeePopulationId)
                )
              },
              other: {
                saved: true,
                values: outputs.configurations.workstations.filter(
                  (w) => !cicPopulationIds.includes(w.employeePopulationId)
                )
              }
            }
          : DEFAULT_WORKSTATION_EDITS
      );
    }
  }, [project, isOutputsLoading, isRefetchingOutputs]);

  useEffect(() => {
    setMeasurementSystem(project?.details?.measurementSystem ?? null);
    resetSiteSpecificSpaces(project?.siteSpecificSpaces ?? []);
  }, [project]);

  const getRowEdits = (spaceType) => rowEdits[spaceType] ?? {};

  const onRowEdit = (spaceType) => (id, value) =>
    setRowEdits((prevEdits) => ({
      ...prevEdits,
      [spaceType]: {
        ...prevEdits[spaceType],
        [id]: {
          value,
          isSaved: _.isEqual(value ?? {}, initialRowEditsRef.current[spaceType]?.[id]?.value ?? {})
        }
      }
    }));

  const handleMeasurementSystemChange = (newMeasurementSystem) => {
    setMeasurementSystem(newMeasurementSystem);
    updateProjectMeasurementSystemMutation.mutate({
      id: projectId,
      measurementSystem: newMeasurementSystem
    });
  };

  const getConfigurations = () =>
    Object.entries(rowEdits).flatMap(([configurationTypeRaw, configurations]) => {
      const configurationType = Number(configurationTypeRaw);
      return Object.entries(configurations)
        .filter(
          ([, configuration]) =>
            !_.isEmpty(_.omit(configuration?.value, 'isExcluded')) || configuration?.value.isExcluded
        )
        .map(([spaceId, { value: configuration }]) => {
          const quantity = configuration.quantity == null ? null : Number(configuration.quantity);
          const isExcluded = configuration.isExcluded || null;

          return {
            spaceId,
            type: configurationType,
            notes: configuration.notes || null,
            isExcluded,
            unitArea: convertToStandardSystem(configuration.unitArea || null, measurementSystem),
            seats: configuration.seats || null,
            quantity:
              isExcluded || configurationType !== OUTPUT_CONFIGURATION_TYPE.specialty
                ? quantity
                : quantity ?? specialtySpacesRoomsById[spaceId]?.defaultQuantity ?? null
          };
        });
    });

  const handleSave = () => {
    onSubmitSiteSpecificSpaces();
    saveProjectOutputConfigurationMutation.mutate({
      id: projectId,
      configurations: {
        spaces: getConfigurations(),
        workstations: [...workstationEdits.cic.values, ...workstationEdits.other.values]
      }
    });
  };

  const handleSaveAs = (projectName) => {
    saveProjectAsMutation.mutate({
      id: projectId,
      saveAs: {
        name: projectName,
        outputConfigurations: getConfigurations(),
        siteSpecificSpaces
      }
    });
    setIsSaveAsModalOpen(false);
  };

  const regularSpacesOutputsAccordionItems = [
    {
      type: 'collaborative',
      title: 'Collaboration spaces',
      description: 'Where IBM teams present, share and co-create ideas and business solutions',
      tableType: 'default'
    },
    {
      type: 'individual',
      title: 'Individual spaces',
      description: 'Where IBM team members absorb, develop and refine ideas',
      tableType: 'default'
    },
    {
      type: 'community',
      title: 'Community spaces',
      description: 'Where IBM team members unwind, reset and converse',
      tableType: 'community'
    },
    {
      type: 'support',
      title: 'Support spaces',
      description: 'Where day-to-day services and activities of IBM team members are supported',
      tableType: 'support'
    }
  ];

  return (
    <div className={styles.wrapper}>
      <SaveProjectAsModal
        open={isSaveAsModalOpen}
        initialProjectName={project?.details.name}
        onCancel={() => setIsSaveAsModalOpen(false)}
        onSave={handleSaveAs}
      />
      {!isExport && (
        <Overview
          measurementSystem={measurementSystem}
          onMeasurementSystemChange={handleMeasurementSystemChange}
          overview={outputs?.overview}
          isLoading={isOutputsLoading || isRefetchingOutputs}
          project={project}
        />
      )}
      <div className={cx(styles.content, !isExport && styles['content--with-padding'])}>
        {!isExport && <ProjectBreadcrumbs projectId={projectId} page={PROJECT_BREADCRUMBS_PAGE.outputs} />}
        {!isExport && (
          <h1>
            <strong>Output of Target Spaces</strong>
          </h1>
        )}
        {!isExport && (
          <Toggle
            id="show-customizations"
            className={styles['show-customizations']}
            labelText="Show Customizations"
            hideLabel
            labelA="Show Customizations"
            labelB="Show Customizations"
            size="sm"
            disabled={isLoading}
            toggled={isShowingCustomizations}
            onToggle={setIsShowingCustomizations}
          />
        )}
        {!isExport && (
          <PageDescription>
            Below is a list of your target spaces categorized by space type, based on your inputs. You may <br />{' '}
            customize quantities as required. Please leave notes to explain any customizations.
          </PageDescription>
        )}
        <OutputsAccordion isLoading={isLoading}>
          {regularSpacesOutputsAccordionItems.map(({ type, title, description, tableType }) => (
            <OutputsAccordionItem
              key={type}
              type={type}
              title={title}
              description={!isExport && description}
              isExport={isExport}
            >
              <RegularSpacesTable
                spaces={spacesByCategory[SPACE_CATEGORY[type]] ?? []}
                outputs={outputsBySpaceId}
                workstations={outputs?.workstations}
                rowEdits={getRowEdits(OUTPUT_CONFIGURATION_TYPE[type])}
                onRowEdit={onRowEdit(OUTPUT_CONFIGURATION_TYPE[type])}
                isShowingCustomizations={isShowingCustomizations}
                type={tableType}
                measurementSystem={measurementSystem}
                isExport={isExport}
                project={project}
                workstationEdits={workstationEdits}
                onWorkstationsEdit={setWorkstationEdits}
              />
            </OutputsAccordionItem>
          ))}
          <OutputsAccordionItem
            type="specialty"
            title="Specialty spaces"
            description={!isExport && 'Predefined special space types that are applicable to a site'}
            isExport={isExport}
          >
            <SpecialtySpacesAccordion
              spaces={specialtySpaces}
              rooms={specialtySpacesRooms}
              workstations={outputs?.workstations}
              projectSpecialtySpaces={project?.specialtySpaces}
              rowEdits={getRowEdits(OUTPUT_CONFIGURATION_TYPE.specialty)}
              onRowEdit={onRowEdit(OUTPUT_CONFIGURATION_TYPE.specialty)}
              isShowingCustomizations={isShowingCustomizations}
              measurementSystem={measurementSystem}
              isExport={isExport}
              project={project}
            />
          </OutputsAccordionItem>
          <OutputsAccordionItem
            type="siteSpecific"
            title="Site-Specific spaces"
            description={
              !isExport &&
              'Unique, one-off space types that are not included in collaboration, individual, community, support, and specialty spaces'
            }
            isExport={isExport}
          >
            <SiteSpecificSpaces
              spaces={siteSpecificSpaces}
              measurementSystem={measurementSystem}
              onAddSpace={addSiteSpecificSpace}
              onEditSpace={editSiteSpecificSpace}
              onRemoveSpace={removeSiteSpecificSpace}
              isExport={isExport}
            />
          </OutputsAccordionItem>
        </OutputsAccordion>
        {!isExport && (
          <div className={styles.footer}>
            <OutputsActions
              projectId={projectId}
              className={styles['footer__actions']}
              onSave={handleSave}
              onSaveAs={() => setIsSaveAsModalOpen(true)}
              isLoading={isLoading}
              isSaving={
                saveProjectOutputConfigurationMutation.isLoading ||
                isRefetchingOutputs ||
                updateProjectSiteSpecificSpacesMutation.isLoading
              }
              isSavingAs={saveProjectAsMutation.isLoading}
            />
            <Link href={routes.allProjects()}>View Project List</Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default Outputs;
